import classNames from "classnames";
import React, { FC, memo, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { Text } from "@/components/Text";
import { ITypeaheadProps } from "@/components/Typeahead";
import { UserContext } from "@/components/User";
import {
  deleteLocationSearchHistory,
  fetchLocationSearchHistory,
  fetchLocationsHints,
  ILocation,
  ILocationGroup,
  MAX_RECENT_SEARCHES,
} from "@/data/locations";
import { Flex, SelectComponents } from "@/eworkitComponents";
import { useToggle } from "@/hooks/Toggle";
import { IFilterComponentProps, useOnChangeFilterHandler } from "@/infrastructure/Filter";
import { SessionContext } from "@/infrastructure/Session";
import { TypeaheadLocationHints } from "@/views/components/Typeaheads";

interface Props<T>
  extends IFilterComponentProps<T>,
    Omit<ITypeaheadProps, keyof IFilterComponentProps<T> | "loadOptions"> {
  showDefault?: boolean;
}

export const LocationFilter: FC<Props<ILocation>> = memo(
  ({ name, onChange, components, showDefault = true, ...rest }) => {
    const handleChange = useOnChangeFilterHandler<ILocation>(name, onChange);
    const { contextConsultantProfile, contextCompany } = useContext(UserContext);
    const { authenticated } = useContext(SessionContext);
    const [defaultLocations, setDefaultLocations] = useState<ILocationGroup[]>([]);
    const [updateSearchHistory, setUpdateSearchHistory] = useToggle(false);
    const { t } = useTranslation("public");
    const baseClass = "location-filter";

    useEffect(() => {
      const fetchProfileCountry = async (countryName: string, countryCode: string) => {
        const profileCountryLocation = await fetchLocationsHints({
          query: countryName,
          authenticated,
          companyId: contextCompany?.id,
          filtered: false,
        });
        return profileCountryLocation.filter(location => {
          return !location.city && location.countryCode === countryCode && location.name === location.country;
        })[0];
      };

      const fetchData = async () => {
        let profileLocationGroup: ILocationGroup[] = [];
        if (contextConsultantProfile?.location) {
          const { coordinates, ...restLocationData } = contextConsultantProfile.location;
          const profileLocations = [restLocationData];
          if (contextConsultantProfile.location.city && contextConsultantProfile.location.countryCode) {
            const profileCountry = await fetchProfileCountry(
              contextConsultantProfile.location.country,
              contextConsultantProfile.location.countryCode,
            );
            if (profileCountry) {
              profileLocations.push(profileCountry);
            }
          }

          profileLocationGroup = [
            {
              label: "LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.SEARCH_FOR",
              options: profileLocations.map(el => ({ ...el, isProfileLocation: true })),
            },
          ];
        }

        const locationsSearchHistoryResponse = await fetchLocationSearchHistory({ companyId: contextCompany?.id });
        setDefaultLocations([
          {
            label: "LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.RECENT_LOCATIONS",
            options: locationsSearchHistoryResponse
              .slice(0, MAX_RECENT_SEARCHES - (profileLocationGroup[0]?.options.length || 0))
              .map(el => ({ ...el, isCustom: true })),
          },
          ...profileLocationGroup,
        ]);
      };
      if (authenticated && showDefault) {
        fetchData();
      }
    }, [authenticated, rest.value, updateSearchHistory]);

    const handleDeleteSearchClick = async (e: any, id: number) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        await deleteLocationSearchHistory({ locationId: id, companyId: contextCompany?.id });
      } catch (e) {
        // TODO what if it wont work?
      }
      setUpdateSearchHistory();
    };

    const RecentGroupHeading = (props: any) => {
      return (
        <Flex padding={["xs"]}>
          <Text color="grey2">{t(props.children)}</Text>
        </Flex>
      );
    };

    const Option = ({ className, data, ...restProps }: any) => {
      return (
        <SelectComponents.Option {...restProps} className={classNames(className, `${baseClass}__option`)}>
          <Flex direction="row">
            <Flex grow={1} direction="row" innerPadding="sm">
              <Icon icon={data.isCustom ? "search" : "pin"} size="md" color="grey4" />
              <Text>
                {data.name}{" "}
                {data.isProfileLocation && (
                  <Text color="grey2">({t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.PROFILE_LOCATION")})</Text>
                )}
              </Text>
            </Flex>
            {data.isCustom && (
              <Button
                variant="clean"
                icon="close"
                iconColor="grey2"
                onClick={e => {
                  handleDeleteSearchClick(e, data.id);
                }}
              />
            )}
          </Flex>
        </SelectComponents.Option>
      );
    };

    const customStyles = {
      group: (provided: any) => ({
        ...provided,
        paddingBottom: 0,
        paddingTop: 0,
        borderTop: "none !important",
      }),
      option: (provided: any) => ({
        ...provided,
        padding: "6px",
      }),
      menuList: (provided: any) => ({
        ...provided,
        maxHeight: "265px !important",
      }),
    };

    return (
      <div className="filter-input">
        <TypeaheadLocationHints
          styles={customStyles}
          labelKey="name"
          valueKey="name"
          name={name}
          isClearable
          onChange={handleChange}
          searchParams={{ filtered: false, companyId: contextCompany?.id, authenticated }}
          components={{ GroupHeading: RecentGroupHeading, Option, ...components }}
          defaultOptions={defaultLocations}
          {...rest}
        />
      </div>
    );
  },
);
